import axios from "../axios";

function normalizeText(value) {
  return value === null || value === undefined ? "" : String(value).trim();
}

function buildApiUrl(path) {
  const base = normalizeText(window.hostname || "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (!base) {
    return normalizedPath;
  }

  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${normalizedBase}${normalizedPath}`;
}

function isObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function parseListResponse(response) {
  return response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
}

export function normalizeWatchlistPriority(value) {
  const normalized = normalizeText(value).toLowerCase();
  return ["high", "normal", "low"].includes(normalized) ? normalized : "normal";
}

export function normalizeWatchlistLicensePlate(value) {
  return normalizeText(value).toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function extractApiErrorMessage(error, fallbackMessage = "Something went wrong. Please try again.") {
  const responseData = error && error.response ? error.response.data : null;

  if (responseData) {
    if (typeof responseData.message === "string" && responseData.message.trim().length > 0) {
      return responseData.message.trim();
    }

    if (isObject(responseData.errors)) {
      const messages = Object.values(responseData.errors)
        .flat()
        .map((message) => normalizeText(message))
        .filter((message) => message.length > 0);

      if (messages.length > 0) {
        return messages.join(" ");
      }
    }
  }

  return fallbackMessage;
}

export function extractValidationErrors(error) {
  const responseData = error && error.response ? error.response.data : null;
  if (!responseData || !isObject(responseData.errors)) {
    return {};
  }

  return Object.keys(responseData.errors).reduce((errors, fieldName) => {
    const fieldValue = responseData.errors[fieldName];
    const messages = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
    const message = messages.map((value) => normalizeText(value)).filter((value) => value.length > 0).join(" ");

    if (message) {
      errors[fieldName] = message;
    }

    return errors;
  }, {});
}

export async function listWatchlistGroups(includeInactive = false) {
  const response = await axios.get(
    buildApiUrl(`/api/auth/watchlist/groups?include_inactive=${includeInactive ? "true" : "false"}`)
  );

  return parseListResponse(response);
}

export async function createWatchlistGroup(payload) {
  return axios.post(buildApiUrl("/api/auth/watchlist/groups"), payload);
}

export async function updateWatchlistGroup(groupId, payload) {
  return axios.patch(buildApiUrl(`/api/auth/watchlist/groups/${groupId}`), payload);
}

export async function deleteWatchlistGroup(groupId) {
  return axios.delete(buildApiUrl(`/api/auth/watchlist/groups/${groupId}`));
}

export async function listWatchlistEntries(groupId, includeInactive = false) {
  const response = await axios.get(
    buildApiUrl(`/api/auth/watchlist/groups/${groupId}/entries?include_inactive=${includeInactive ? "true" : "false"}`)
  );

  return parseListResponse(response);
}

export async function createWatchlistEntry(groupId, payload) {
  return axios.post(buildApiUrl(`/api/auth/watchlist/groups/${groupId}/entries`), payload);
}

export async function updateWatchlistEntry(groupId, entryId, payload) {
  return axios.patch(buildApiUrl(`/api/auth/watchlist/groups/${groupId}/entries/${entryId}`), payload);
}

export async function deleteWatchlistEntry(groupId, entryId) {
  return axios.delete(buildApiUrl(`/api/auth/watchlist/groups/${groupId}/entries/${entryId}`));
}